import { useState, useRef } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, FileText, FileSpreadsheet, ChevronLeft, ChevronRight, Send, FileBarChart2, RotateCcw, CheckCircle2, Paperclip, X, Download, MessageSquare, Clock, AlertTriangle } from "lucide-react";
import { Badge } from "../components/ui/badge";
import * as XLSX from "xlsx";
import { ReorderableTable, type ReorderableColumn } from "@/components/ReorderableTable";

const MODOS_PAGAMENTO = [
  "01-Dinheiro", "02-Cheque", "03-Cartão de Crédito", "04-Cartão de Débito",
  "05-Crédito Loja", "10-Vale Alimentação", "11-Vale Refeição", "12-Vale Presente",
  "13-Vale Combustível", "15-Boleto Bancário", "16-Depósito Bancário", "17-PIX",
  "18-Transferência Bancária / Carteira Digital", "19-Programa de Fidelidade / Cashback",
  "90-Sem Pagamento", "99-Outros",
];

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pendente: { label: "Pendente", color: "bg-yellow-100 text-yellow-800" },
  pago: { label: "Pago", color: "bg-green-100 text-green-800" },
  excluido: { label: "Excluído", color: "bg-red-100 text-red-800" },
};

const emptyForm = {
  emitenteId: 0,
  destinatarioId: 0,
  pedidoId: "",
  numeroParcela: 1,
  totalParcelas: 1,
  descricao: "",
  tipoPagamento: "",
  valorParcela: 0,
  dataVencimento: "",
  anotacaoInicial: "",
};

const emptyEdit = {
  id: 0,
  descricao: "",
  tipoPagamento: "",
  valorParcela: 0,
  dataVencimento: "",
  dataPromessaPagamento: "",
  status: "pendente",
  dataRecebimento: "",
  valorRecebido: 0,
};

type SortDirection = "asc" | "desc";

export default function AccountsReceivable() {
  const { user } = useGoogleAuth();
  const emitenteId = user?.emitenteId;

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    somenteComAnexo: false,
    somenteSemAnexo: false,
    dataVencimentoDe: "",
    dataVencimentoAte: "",
  });
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortDir, setSortDir] = useState<SortDirection>("asc");
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editForm, setEditForm] = useState({ ...emptyEdit });
  const [showDestinatarioModal, setShowDestinatarioModal] = useState(false);
  const [destinatarioSearch, setDestinatarioSearch] = useState("");
  const [destinatarioPage, setDestinatarioPage] = useState(1);
  const [destinatarioNome, setDestinatarioNome] = useState("");

  // Estados para geração de boleto
  const [showBoletoModal, setShowBoletoModal] = useState(false);
  const [boletoParcelaId, setBoletoParcelaId] = useState<number | null>(null);
  const [boletoContaBancariaId, setBoletoContaBancariaId] = useState<string>("");
  const [boletoGerado, setBoletoGerado] = useState<any>(null);

  // Estados para baixa e arquivos
  const [showBaixaModal, setShowBaixaModal] = useState(false);
  const [baixaParcelaId, setBaixaParcelaId] = useState<number | null>(null);
  const [showArquivosModal, setShowArquivosModal] = useState(false);
  const [arquivosParcelaId, setArquivosParcelaId] = useState<number | null>(null);
  const [showAnotacoesModal, setShowAnotacoesModal] = useState(false);
  const [anotacoesParcelaId, setAnotacoesParcelaId] = useState<number | null>(null);
  const [periodoPagoDias, setPeriodoPagoDias] = useState<"3" | "7" | "30">("30");

  const queryParams = {
    emitenteId: emitenteId ?? undefined,
    search: searchTerm || undefined,
    status: filters.status || undefined,
    somenteComAnexo: filters.somenteComAnexo || undefined,
    somenteSemAnexo: filters.somenteSemAnexo || undefined,
    dataVencimentoDe: filters.dataVencimentoDe || undefined,
    dataVencimentoAte: filters.dataVencimentoAte || undefined,
    page,
    pageSize: 20,
    sortBy,
    sortDir,
  };

  const indicadoresParams = {
    emitenteId: emitenteId ?? undefined,
    search: searchTerm || undefined,
    status: filters.status || undefined,
    somenteComAnexo: filters.somenteComAnexo || undefined,
    somenteSemAnexo: filters.somenteSemAnexo || undefined,
    dataVencimentoDe: filters.dataVencimentoDe || undefined,
    dataVencimentoAte: filters.dataVencimentoAte || undefined,
    periodoPagoDias: Number(periodoPagoDias) as 3 | 7 | 30,
  };

  const { data, refetch: refetchLista, isLoading } = trpc.accountsReceivable.list.useQuery(queryParams);
  const { data: indicadores, refetch: refetchIndicadores } = trpc.accountsReceivable.indicadores.useQuery(indicadoresParams);
  const refetch = () => Promise.all([refetchLista(), refetchIndicadores()]);
  const utils = trpc.useUtils();
  const { data: emitentes } = trpc.emitentes.list.useQuery();
  const destinatarioEmitenteId = (emitenteId ?? form.emitenteId) || undefined;
  const { data: destinatariosData } = trpc.destinatarios.list.useQuery({
    emitenteId: destinatarioEmitenteId,
    page: destinatarioPage,
    limit: 20,
    search: destinatarioSearch || undefined,
  }, { enabled: !!destinatarioEmitenteId });
  const destinatariosList = (destinatariosData as any)?.data ?? [];
  const destinatariosTotal = Number((destinatariosData as any)?.total ?? 0);
  const destinatariosTotalPages = Number((destinatariosData as any)?.totalPages ?? 0);
  const { data: contasBancarias } = trpc.bankAccounts.list.useQuery({ emitenteId: emitenteId ?? undefined });

  // Busca boleto existente para a parcela selecionada
  const { data: boletoExistente, refetch: refetchBoleto } = trpc.boletos.getByParcela.useQuery(
    { parcelaId: boletoParcelaId! },
    { enabled: !!boletoParcelaId }
  );

  const createMutation = trpc.accountsReceivable.create.useMutation({
    onSuccess: () => { toast.success("Parcela criada com sucesso!"); setShowCreate(false); setForm({ ...emptyForm }); setDestinatarioNome(""); setDestinatarioSearch(""); setDestinatarioPage(1); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = trpc.accountsReceivable.update.useMutation({
    onSuccess: () => { toast.success("Parcela atualizada!"); setShowEdit(false); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const enviarCobrancaMutation = trpc.accountsReceivable.enviarCobranca.useMutation({
    onSuccess: (data) => {
      if (data.aviso) {
        toast.info(data.aviso);
      } else {
        toast.success("Cobrança enviada com sucesso!");
      }
    },
    onError: (err) => toast.error(err.message || "Erro ao enviar cobrança"),
  });

  const excluirMutation = trpc.accountsReceivable.excluir.useMutation({
    onSuccess: () => { toast.success("Parcela excluída!"); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const gerarBoletoMutation = trpc.boletos.gerarParaParcela.useMutation({
    onSuccess: (data) => {
      toast.success("Boleto gerado com sucesso!");
      setBoletoGerado(data);
      refetchBoleto();
    },
    onError: (e) => toast.error(e.message || "Erro ao gerar boleto"),
  });

  const reemitirBoletoMutation = trpc.boletos.reemitir.useMutation({
    onSuccess: (data) => {
      toast.success("Boleto reemitido com sucesso!");
      setBoletoGerado(data);
      refetchBoleto();
    },
    onError: (e) => toast.error(e.message || "Erro ao reemitir boleto"),
  });

  const rows = data?.rows ?? [];
  const totalPages = data?.totalPages ?? 1;
  const total = data?.total ?? 0;

  const alternarOrdenacao = (campo: string) => {
    if (sortBy === campo) setSortDir((direcao) => direcao === "asc" ? "desc" : "asc");
    else { setSortBy(campo); setSortDir("asc"); }
    setPage(1);
  };

  // Contas bancárias ativas para boleto
  const contasBancariasAtivas = (contasBancarias ?? []).filter((c: any) =>
    c.ativo && ['341', '237', '033'].includes(c.codigoBanco)
  );

  function handleEdit(row: any) {
    setEditForm({
      id: row.id,
      descricao: row.descricao ?? "",
      tipoPagamento: row.tipoPagamento ?? "",
      valorParcela: parseFloat(row.valorParcela),
      dataVencimento: row.dataVencimento?.split("T")[0] ?? "",
      dataPromessaPagamento: row.dataPromessaPagamento?.split("T")[0] ?? "",
      status: row.status,
      dataRecebimento: row.dataRecebimento?.split("T")[0] ?? "",
      valorRecebido: row.valorRecebido ? parseFloat(row.valorRecebido) : 0,
    });
    setShowEdit(true);
  }

  function handleAbrirBoleto(row: any) {
    setBoletoParcelaId(row.id);
    setBoletoContaBancariaId("");
    setBoletoGerado(null);
    setShowBoletoModal(true);
  }

  async function handleExportPDF() {
    if (!total) {
      toast.info("Nenhuma parcela encontrada para os filtros atuais.");
      return;
    }

    try {
      const exportData = await utils.accountsReceivable.list.fetch({
        ...queryParams,
        page: 1,
        pageSize: total,
      });
      const exportRows = exportData?.rows ?? [];
      const jsPDF = (await import("jspdf")).default;
      const autoTable = (await import("jspdf-autotable")).default;
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const totalValor = exportRows.reduce((soma: number, row: any) => soma + (parseFloat(row.valorParcela) || 0), 0);

      doc.setFontSize(15);
      doc.setFont("helvetica", "bold");
      doc.text("CONTAS A RECEBER", 148.5, 14, { align: "center" });
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(`${exportRows.length} registro(s)`, 14, 22);
      doc.text(`Gerado em ${new Date().toLocaleString("pt-BR")}`, 283, 22, { align: "right" });

      autoTable(doc, {
        startY: 27,
        head: [["ID", "Emitente", "Destinatário", "Descrição", "Pagamento", "Valor", "Vencimento", "NF-e", "Status"]],
        body: exportRows.map((row: any) => [
          row.id,
          row.emitenteNome ?? "-",
          row.destinatarioNome ?? "-",
          row.descricao ?? "-",
          row.tipoPagamento ?? "-",
          formatCurrency(row.valorParcela),
          formatDate(row.dataVencimento),
          row.numeroNfe ?? "-",
          STATUS_LABELS[row.status]?.label ?? row.status ?? "-",
        ]),
        styles: { fontSize: 7, cellPadding: 1.5, overflow: "linebreak" },
        headStyles: { fillColor: [31, 41, 55], textColor: 255, fontStyle: "bold" },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 38 },
          2: { cellWidth: 38 },
          3: { cellWidth: 48 },
          4: { cellWidth: 29 },
          5: { cellWidth: 20, halign: "right" },
          6: { cellWidth: 22 },
          7: { cellWidth: 18 },
          8: { cellWidth: 18 },
        },
        didDrawPage: (tableData: any) => {
          doc.setFontSize(7);
          doc.text(`Página ${tableData.pageNumber}`, 283, 204, { align: "right" });
        },
      });

      let totalY = ((doc as any).lastAutoTable?.finalY ?? 27) + 8;
      if (totalY > 198) {
        doc.addPage();
        totalY = 16;
      }
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(`Total: ${formatCurrency(totalValor)}`, 283, totalY, { align: "right" });
      doc.save("contas_receber.pdf");
      toast.success("PDF exportado com todos os registros filtrados.");
    } catch {
      toast.error("Erro ao gerar o PDF de Contas a Receber.");
    }
  }

  async function handleExportExcel() {
    if (!total) {
      toast.info("Nenhuma parcela encontrada para os filtros atuais.");
      return;
    }

    try {
      const exportData = await utils.accountsReceivable.list.fetch({
        ...queryParams,
        page: 1,
        pageSize: total,
      });
      const exportRows = exportData?.rows ?? [];
      const toExcelDate = (value: string | null | undefined) => value ? new Date(`${value}T12:00:00`) : "";
      const worksheet = XLSX.utils.aoa_to_sheet([
        ["ID", "Destinatário", "CNPJ", "Descrição", "Tipo Pagamento", "Valor", "Vencimento", "Status", "Data Recebimento", "Valor Recebido", "NF-e"],
        ...exportRows.map((row: any) => [
          row.id,
          row.destinatarioNome ?? "",
          row.destinatarioCnpj ?? "",
          row.descricao ?? "",
          row.tipoPagamento ?? "",
          parseFloat(row.valorParcela) || 0,
          toExcelDate(row.dataVencimento),
          row.status ?? "",
          toExcelDate(row.dataRecebimento),
          row.valorRecebido == null || row.valorRecebido === "" ? "" : parseFloat(row.valorRecebido) || 0,
          row.numeroNfe ?? "",
        ]),
      ]);

      for (let row = 2; row <= exportRows.length + 1; row += 1) {
        if (worksheet[`F${row}`]) worksheet[`F${row}`].z = '[$R$-416] #,##0.00';
        if (worksheet[`G${row}`]) worksheet[`G${row}`].z = "dd/mm/yyyy";
        if (worksheet[`I${row}`]) worksheet[`I${row}`].z = "dd/mm/yyyy";
        if (worksheet[`J${row}`]) worksheet[`J${row}`].z = '[$R$-416] #,##0.00';
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Contas a Receber");
      XLSX.writeFile(workbook, "contas_receber.xlsx");
      toast.success("Arquivo Excel exportado!");
    } catch {
      toast.error("Erro ao gerar o Excel de Contas a Receber.");
    }
  }

  function formatCurrency(v: any) {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(parseFloat(v) || 0);
  }

  function formatDate(d: any) {
    if (!d) return "";
    return new Date(d.toString().includes("T") ? d : d + "T12:00:00").toLocaleDateString("pt-BR");
  }

  function formatDateTime(d: any) {
    if (!d) return "-";
    return new Date(d.toString().includes("T") ? d : d + "T12:00:00").toLocaleString("pt-BR");
  }

  function isBoleto(tipoPagamento: string | null | undefined) {
    if (!tipoPagamento) return false;
    return tipoPagamento.toLowerCase().includes("boleto") || tipoPagamento === "15-Boleto Bancário";
  }

  const receivableColumns: ReorderableColumn<any>[] = [
    { id: "id", fixed: true, header: "ID", sortKey: "id", headerClassName: "pb-2 pr-3", className: "py-2 pr-3 text-gray-500", renderCell: (row) => row.id },
    { id: "pedido", header: "Pedido", sortKey: "pedidoId", headerClassName: "pb-2 pr-3", className: "py-2 pr-3", renderCell: (row) => row.pedidoId ?? "-" },
    { id: "referencia", header: "Referência da Importação", sortKey: "referenciaBox", headerClassName: "pb-2 pr-3", className: "py-2 pr-3", renderCell: (row) => row.referenciaBox ?? "-" },
    { id: "parcela", header: "Parcela", sortKey: "numeroParcela", headerClassName: "pb-2 pr-3", className: "py-2 pr-3", renderCell: (row) => row.numeroParcela != null ? `${row.numeroParcela}/${row.totalParcelas ?? 1}` : "-" },
    { id: "emitente", header: "Emitente", sortKey: "emitenteNome", headerClassName: "pb-2 pr-3", className: "py-2 pr-3", renderCell: (row) => row.emitenteNome ?? "-" },
    { id: "destinatario", header: "Destinatário", sortKey: "destinatarioNome", headerClassName: "pb-2 pr-3", className: "py-2 pr-3 font-medium", renderCell: (row) => row.destinatarioNome ?? "-" },
    { id: "descricao", header: "Descrição", sortKey: "descricao", headerClassName: "pb-2 pr-3", className: "py-2 pr-3", renderCell: (row) => row.descricao ?? "-" },
    { id: "pagamento", header: "Pagamento", sortKey: "tipoPagamento", headerClassName: "pb-2 pr-3", className: "py-2 pr-3", renderCell: (row) => row.tipoPagamento ?? "-" },
    { id: "valor", header: "Valor", sortKey: "valorParcela", headerClassName: "pb-2 pr-3 text-right", className: "py-2 pr-3 text-right font-medium", renderCell: (row) => formatCurrency(row.valorParcela) },
    { id: "vencimento", header: "Vencimento", sortKey: "dataVencimento", headerClassName: "pb-2 pr-3", className: "py-2 pr-3", renderCell: (row) => formatDate(row.dataVencimento) },
    { id: "promessa", header: "Promessa de Pagamento", sortKey: "dataPromessaPagamento", headerClassName: "pb-2 pr-3", className: "py-2 pr-3", renderCell: (row) => row.dataPromessaPagamento ? formatDate(row.dataPromessaPagamento) : "-" },
    { id: "nfe", header: "NF-e", sortKey: "numeroNfe", headerClassName: "pb-2 pr-3", className: "py-2 pr-3", renderCell: (row) => row.numeroNfe ?? "-" },
    { id: "status", header: "Status", sortKey: "status", headerClassName: "pb-2 pr-3", className: "py-2 pr-3", renderCell: (row) => { const st = STATUS_LABELS[row.status] ?? { label: row.status, color: "bg-gray-100 text-gray-800" }; return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>; } },
    { id: "ultimaAtualizacao", header: "Última Atualização", sortKey: "atualizadoEm", headerClassName: "pb-2 pr-3", className: "py-2 pr-3", renderCell: (row) => <div><div>{formatDateTime(row.atualizadoEm)}</div><div className="text-xs text-gray-500">{row.atualizadoPorNome ?? "-"}</div></div> },
    { id: "anotacoes", header: "Anotações", headerClassName: "pb-2 pr-3", className: "py-2 pr-3", renderCell: (row) => <button className="text-gray-400 hover:text-blue-600" title="Anotações" onClick={() => { setAnotacoesParcelaId(row.id); setShowAnotacoesModal(true); }}><MessageSquare className="w-3.5 h-3.5" /></button> },
    { id: "arquivos", header: "Arquivos", headerClassName: "pb-2 pr-3", className: "py-2 pr-3", renderCell: (row) => <button className="text-gray-400 hover:text-blue-600" title="Arquivos e comprovantes" onClick={() => { setArquivosParcelaId(row.id); setShowArquivosModal(true); }}><Paperclip className="w-3.5 h-3.5" /></button> },
    { id: "acoes", header: "Ações", headerClassName: "pb-2", className: "py-2", renderCell: (row) => <div className="flex gap-1">{row.status !== "pago" && row.status !== "excluido" && <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600 hover:text-green-800" title="Dar Baixa" onClick={() => { setBaixaParcelaId(row.id); setShowBaixaModal(true); }}><CheckCircle2 className="w-3.5 h-3.5" /></Button>}{row.pedidoId && <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500 hover:text-blue-700" title="Enviar cobrança" disabled={enviarCobrancaMutation.isPending} onClick={() => { if (confirm(`Enviar cobrança do pedido #${row.pedidoId} para o destinatário?`)) enviarCobrancaMutation.mutate({ pedidoId: row.pedidoId }); }}><Send className="w-3.5 h-3.5" /></Button>}{isBoleto(row.tipoPagamento) && row.status !== "excluido" && <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600 hover:text-green-800" title="Gerar / Ver Boleto" onClick={() => handleAbrirBoleto(row)}><FileBarChart2 className="w-3.5 h-3.5" /></Button>}<Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(row)}><Pencil className="w-3.5 h-3.5" /></Button>{row.status !== "excluido" && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => { if (confirm("Excluir esta parcela?")) excluirMutation.mutate({ id: row.id }); }}><Trash2 className="w-3.5 h-3.5" /></Button>}</div> },
  ];

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Contas a Receber</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <FileText className="w-4 h-4 mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4 mr-1" /> Nova Parcela
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="h-8 w-8 text-yellow-500" />
            <div>
              <p className="text-sm text-gray-500">Total em aberto</p>
              <p className="text-xl font-bold">{formatCurrency(indicadores?.totalEmAberto)}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-red-500" />
            <div>
              <p className="text-sm text-gray-500">Vencido ({Number(indicadores?.percentualVencido ?? 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%)</p>
              <p className="text-xl font-bold text-red-600">{formatCurrency(indicadores?.totalVencido)}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle2 className="h-8 w-8 text-green-500" />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-500 whitespace-nowrap">Pago no período</p>
                <Select value={periodoPagoDias} onValueChange={(value) => setPeriodoPagoDias(value as "3" | "7" | "30")}>
                  <SelectTrigger className="h-7 w-[92px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 dias</SelectItem>
                    <SelectItem value="7">7 dias</SelectItem>
                    <SelectItem value="30">30 dias</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xl font-bold text-green-600">{formatCurrency(indicadores?.totalPagoPeriodo)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="mb-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Filtros:</span>
              <Input
                placeholder="Buscar por qualquer coisa..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                className="max-w-md"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label>Status</Label>
              <Select value={filters.status || "todos"} onValueChange={(v) => { setFilters(f => ({ ...f, status: v === "todos" ? "" : v })); setPage(1); }}>
                <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="pago">Pago</SelectItem>
                  <SelectItem value="excluido">Excluído</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Anexo</Label>
              <Select value={filters.somenteComAnexo ? "com_anexo" : filters.somenteSemAnexo ? "sem_anexo" : "todos"} onValueChange={(v) => { setFilters(f => ({ ...f, somenteComAnexo: v === "com_anexo", somenteSemAnexo: v === "sem_anexo" })); setPage(1); }}>
                <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="com_anexo">Com anexo</SelectItem>
                  <SelectItem value="sem_anexo">Sem anexo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vencimento de</Label>
              <Input type="date" value={filters.dataVencimentoDe} onChange={e => { setFilters(f => ({ ...f, dataVencimentoDe: e.target.value })); setPage(1); }} />
            </div>
            <div>
              <Label>Vencimento até</Label>
              <Input type="date" value={filters.dataVencimentoAte} onChange={e => { setFilters(f => ({ ...f, dataVencimentoAte: e.target.value })); setPage(1); }} />
            </div>
            <div className="flex items-end">
              <Button variant="outline" size="sm" onClick={() => { setFilters({ status: "", somenteComAnexo: false, somenteSemAnexo: false, dataVencimentoDe: "", dataVencimentoAte: "" }); setSearchTerm(""); setPage(1); }}>
                <Search className="w-4 h-4 mr-1" /> Limpar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {total} registro{total !== 1 ? "s" : ""} encontrado{total !== 1 ? "s" : ""}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center text-gray-500 py-8">Carregando...</p>
          ) : (
            <div className="overflow-x-auto">
              <ReorderableTable
                pageKey="accounts-receivable-main"
                userId={user?.id}
                columns={receivableColumns}
                rows={rows}
                getRowKey={(row) => row.id}
                getRowProps={() => ({ className: "border-b hover:bg-gray-50" })}
                sort={{ column: sortBy ?? "", direction: sortDir, onToggle: alternarOrdenacao }}
                tableClassName="w-full text-sm"
                headClassName="border-b text-left text-gray-600"
                emptyMessage="Nenhuma parcela encontrada."
                emptyClassName="text-center text-gray-500 py-8"
              />
            </div>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">Página {page} de {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Criar */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nova Parcela Manual</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!emitenteId && (
              <div>
                <Label>Emitente *</Label>
                <Select value={form.emitenteId ? String(form.emitenteId) : ""} onValueChange={v => {
                  setForm(f => ({ ...f, emitenteId: parseInt(v), destinatarioId: 0 }));
                  setDestinatarioNome("");
                  setDestinatarioSearch("");
                  setDestinatarioPage(1);
                }}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {(emitentes ?? []).map((e: any) => <SelectItem key={e.id} value={String(e.id)}>{e.razaoSocial}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Destinatário *</Label>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start font-normal"
                disabled={!destinatarioEmitenteId}
                onClick={() => {
                  setDestinatarioSearch("");
                  setDestinatarioPage(1);
                  setShowDestinatarioModal(true);
                }}
              >
                {destinatarioNome || "Selecionar destinatário"}
              </Button>
            </div>
            <div>
              <Label>Pedido # (opcional)</Label>
              <Input type="number" min={1} step={1} value={form.pedidoId} onChange={e => setForm(f => ({ ...f, pedidoId: e.target.value }))} placeholder="Ex.: 12345" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Parcela Nº *</Label>
                <Input type="number" min={1} value={form.numeroParcela} onChange={e => setForm(f => ({ ...f, numeroParcela: parseInt(e.target.value) || 1 }))} />
              </div>
              <div>
                <Label>Total de Parcelas *</Label>
                <Input type="number" min={1} value={form.totalParcelas} onChange={e => setForm(f => ({ ...f, totalParcelas: parseInt(e.target.value) || 1 }))} />
              </div>
            </div>
            <div>
              <Label>Descrição</Label>
              <Input value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} placeholder="Ex: Parcela 1/3 - Pedido #42" />
            </div>
            <div>
              <Label>Tipo de Pagamento</Label>
              <Select value={form.tipoPagamento || "none"} onValueChange={v => setForm(f => ({ ...f, tipoPagamento: v === "none" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Não informado</SelectItem>
                  {MODOS_PAGAMENTO.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Valor *</Label>
                <Input type="number" step="0.01" min={0} value={form.valorParcela} onChange={e => setForm(f => ({ ...f, valorParcela: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <Label>Vencimento *</Label>
                <Input type="date" value={form.dataVencimento} onChange={e => setForm(f => ({ ...f, dataVencimento: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Anotação inicial</Label>
              <textarea className="border rounded px-2 py-1.5 text-sm w-full h-16 resize-none" value={form.anotacaoInicial} onChange={e => setForm(f => ({ ...f, anotacaoInicial: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
              <Button onClick={() => createMutation.mutate({
                emitenteId: emitenteId ?? form.emitenteId,
                destinatarioId: form.destinatarioId,
                pedidoId: form.pedidoId ? Number(form.pedidoId) : null,
                numeroParcela: form.numeroParcela,
                totalParcelas: form.totalParcelas,
                descricao: form.descricao || null,
                tipoPagamento: form.tipoPagamento || null,
                valorParcela: form.valorParcela,
                dataVencimento: form.dataVencimento,
                anotacaoInicial: form.anotacaoInicial || undefined,
              })} disabled={createMutation.isPending}>
                {createMutation.isPending ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDestinatarioModal} onOpenChange={setShowDestinatarioModal}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Selecionar Destinatário</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Buscar por razão social, fantasia, CNPJ ou CPF..."
              value={destinatarioSearch}
              onChange={(e) => {
                setDestinatarioSearch(e.target.value);
                setDestinatarioPage(1);
              }}
            />
            <div className="border rounded-lg">
              <div className="max-h-[60vh] overflow-auto">
                <table className="w-full border-collapse" style={{ minWidth: "650px" }}>
                  <thead className="sticky top-0 bg-muted">
                    <tr>
                      <th className="border-b p-3 text-left">Razão Social</th>
                      <th className="border-b p-3 text-left">Fantasia</th>
                      <th className="border-b p-3 text-left">CNPJ/CPF</th>
                      <th className="border-b p-3 text-center">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {destinatariosList.map((d: any) => (
                      <tr key={d.id} className="hover:bg-muted/50">
                        <td className="border-b p-3">{d.razaoSocial || "-"}</td>
                        <td className="border-b p-3">{d.nomeFantasia || "-"}</td>
                        <td className="border-b p-3 whitespace-nowrap">{d.cnpj || d.cpf || "-"}</td>
                        <td className="border-b p-3 text-center">
                          <Button size="sm" onClick={() => {
                            setForm(f => ({ ...f, destinatarioId: Number(d.id) }));
                            setDestinatarioNome(d.razaoSocial || d.nomeFantasia || `ID: ${d.id}`);
                            setShowDestinatarioModal(false);
                            setDestinatarioSearch("");
                            setDestinatarioPage(1);
                          }}>Selecionar</Button>
                        </td>
                      </tr>
                    ))}
                    {destinatariosList.length === 0 && (
                      <tr><td colSpan={4} className="p-6 text-center text-sm text-muted-foreground">Nenhum destinatário encontrado.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {destinatariosTotalPages > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-t bg-muted/30">
                  <div className="text-sm text-muted-foreground">
                    Página {destinatarioPage} de {destinatariosTotalPages} ({destinatariosTotal} destinatários)
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setDestinatarioPage(p => Math.max(1, p - 1))} disabled={destinatarioPage === 1}>Anterior</Button>
                    <Button size="sm" variant="outline" onClick={() => setDestinatarioPage(p => Math.min(destinatariosTotalPages, p + 1))} disabled={destinatarioPage >= destinatariosTotalPages}>Próxima</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Editar */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Editar Parcela #{editForm.id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Descrição</Label>
              <Input value={editForm.descricao} onChange={e => setEditForm(f => ({ ...f, descricao: e.target.value }))} />
            </div>
            <div>
              <Label>Tipo de Pagamento</Label>
              <Select value={editForm.tipoPagamento || "none"} onValueChange={v => setEditForm(f => ({ ...f, tipoPagamento: v === "none" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Não informado</SelectItem>
                  {MODOS_PAGAMENTO.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Valor</Label>
                <Input type="number" step="0.01" value={editForm.valorParcela} onChange={e => setEditForm(f => ({ ...f, valorParcela: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <Label>Vencimento</Label>
                <Input type="date" value={editForm.dataVencimento} onChange={e => setEditForm(f => ({ ...f, dataVencimento: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Promessa de Pagamento</Label>
              <Input type="date" value={editForm.dataPromessaPagamento} onChange={e => setEditForm(f => ({ ...f, dataPromessaPagamento: e.target.value }))} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={v => setEditForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="pago">Pago</SelectItem>
                  <SelectItem value="excluido">Excluído</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editForm.status === "pago" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Data Recebimento</Label>
                  <Input type="date" value={editForm.dataRecebimento} onChange={e => setEditForm(f => ({ ...f, dataRecebimento: e.target.value }))} />
                </div>
                <div>
                  <Label>Valor Recebido</Label>
                  <Input type="number" step="0.01" value={editForm.valorRecebido} onChange={e => setEditForm(f => ({ ...f, valorRecebido: parseFloat(e.target.value) || 0 }))} />
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowEdit(false)}>Cancelar</Button>
              <Button onClick={() => updateMutation.mutate({
                id: editForm.id,
                descricao: editForm.descricao || null,
                tipoPagamento: editForm.tipoPagamento || null,
                valorParcela: editForm.valorParcela,
                dataVencimento: editForm.dataVencimento,
                dataPromessaPagamento: editForm.dataPromessaPagamento || null,
                status: editForm.status,
                dataRecebimento: editForm.dataRecebimento || null,
                valorRecebido: editForm.valorRecebido || null,
              })} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Boleto */}
      <Dialog open={showBoletoModal} onOpenChange={(open) => {
        setShowBoletoModal(open);
        if (!open) { setBoletoParcelaId(null); setBoletoGerado(null); setBoletoContaBancariaId(""); }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Boleto Bancário — Parcela #{boletoParcelaId}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Boleto existente */}
            {boletoExistente && !boletoGerado && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                <p className="text-sm font-semibold text-green-800">Boleto já gerado</p>
                <div className="text-xs text-gray-700 space-y-1">
                  <p><span className="font-medium">Nosso Número:</span> {boletoExistente.nossoNumero}</p>
                  <p><span className="font-medium">Linha Digitável:</span> <span className="font-mono">{boletoExistente.linhaDigitavel}</span></p>
                  <p><span className="font-medium">Status:</span> {boletoExistente.status}</p>
                  <p><span className="font-medium">Banco:</span> {boletoExistente.nomeBanco} ({boletoExistente.codigoBanco})</p>
                </div>
                {boletoExistente.pdfPath && (
                  <a
                    href={boletoExistente.pdfPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                  >
                    <FileText className="w-4 h-4" /> Visualizar PDF do Boleto
                  </a>
                )}
                <div className="pt-2">
                  <p className="text-xs text-gray-500 mb-2">Para reemitir, selecione a conta bancária e clique em Reemitir:</p>
                  <Select value={boletoContaBancariaId} onValueChange={setBoletoContaBancariaId}>
                    <SelectTrigger><SelectValue placeholder="Selecione a conta bancária" /></SelectTrigger>
                    <SelectContent>
                      {contasBancariasAtivas.map((c: any) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.nomeBanco} ({c.codigoBanco}) — Ag. {c.agencia} / Cta. {c.conta}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    className="mt-2 w-full"
                    variant="outline"
                    disabled={!boletoContaBancariaId || reemitirBoletoMutation.isPending}
                    onClick={() => {
                      if (confirm("Isso cancelará o boleto anterior e gerará um novo. Confirmar?")) {
                        reemitirBoletoMutation.mutate({ boletoId: boletoExistente.id });
                      }
                    }}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {reemitirBoletoMutation.isPending ? "Reemitindo..." : "Reemitir Boleto"}
                  </Button>
                </div>
              </div>
            )}

            {/* Boleto recém gerado */}
            {boletoGerado && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                <p className="text-sm font-semibold text-blue-800">Boleto gerado com sucesso!</p>
                <div className="text-xs text-gray-700 space-y-1">
                  <p><span className="font-medium">Nosso Número:</span> {boletoGerado.nossoNumero}</p>
                  <p><span className="font-medium">Linha Digitável:</span> <span className="font-mono break-all">{boletoGerado.linhaDigitavel}</span></p>
                  <p><span className="font-medium">Código de Barras:</span> <span className="font-mono break-all text-xs">{boletoGerado.codigoBarras}</span></p>
                </div>
                {boletoGerado.pdfPath && (
                  <a
                    href={boletoGerado.pdfPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                  >
                    <FileText className="w-4 h-4" /> Visualizar PDF do Boleto
                  </a>
                )}
              </div>
            )}

            {/* Gerar novo boleto (quando não há boleto existente) */}
            {!boletoExistente && !boletoGerado && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">Nenhum boleto gerado para esta parcela. Selecione a conta bancária para gerar:</p>
                {contasBancariasAtivas.length === 0 ? (
                  <p className="text-sm text-red-500">Nenhuma conta bancária configurada para boleto (Itaú 341, Bradesco 237 ou Santander 033).</p>
                ) : (
                  <>
                    <Select value={boletoContaBancariaId} onValueChange={setBoletoContaBancariaId}>
                      <SelectTrigger><SelectValue placeholder="Selecione a conta bancária" /></SelectTrigger>
                      <SelectContent>
                        {contasBancariasAtivas.map((c: any) => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.nomeBanco} ({c.codigoBanco}) — Ag. {c.agencia} / Cta. {c.conta}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      className="w-full"
                      disabled={!boletoContaBancariaId || gerarBoletoMutation.isPending}
                      onClick={() => {
                        if (boletoParcelaId && boletoContaBancariaId) {
                          gerarBoletoMutation.mutate({
                            parcelaId: boletoParcelaId,
                            contaBancariaId: parseInt(boletoContaBancariaId),
                          });
                        }
                      }}
                    >
                      <FileBarChart2 className="w-4 h-4 mr-2" />
                      {gerarBoletoMutation.isPending ? "Gerando..." : "Gerar Boleto"}
                    </Button>
                  </>
                )}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setShowBoletoModal(false)}>Fechar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {showBaixaModal && baixaParcelaId && (
        <ModalBaixaReceber id={baixaParcelaId} onClose={() => { setShowBaixaModal(false); setBaixaParcelaId(null); refetch(); }} />
      )}
      {showArquivosModal && arquivosParcelaId && (
        <ModalArquivosReceber id={arquivosParcelaId} onClose={() => { setShowArquivosModal(false); setArquivosParcelaId(null); }} />
      )}
      {showAnotacoesModal && anotacoesParcelaId && (
        <ModalAnotacoesReceber id={anotacoesParcelaId} onClose={() => { setShowAnotacoesModal(false); setAnotacoesParcelaId(null); }} />
      )}
    </div>
  );
}

// ============================================================
// MODAL BAIXA RECEBER
// ============================================================
function ModalBaixaReceber({ id, onClose }: { id: number; onClose: () => void }) {
  const utils = trpc.useUtils();
  const conta = trpc.accountsReceivable.getById.useQuery({ id });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [form, setForm] = useState({
    dataRecebimento: new Date().toISOString().split("T")[0],
    valorRecebido: 0,
    anotacao: "",
  });

  const addArquivoMut = trpc.accountsReceivable.arquivos.add.useMutation({
    onError: (e) => toast.error(e.message),
  });

  const baixarMut = trpc.accountsReceivable.baixar.useMutation({
    onSuccess: () => { toast.success("Baixa realizada com sucesso!"); utils.accountsReceivable.invalidate(); onClose(); },
    onError: (e) => toast.error(e.message),
  });

  const [valorPreenchido, setValorPreenchido] = useState(false);
  if (conta.data && !valorPreenchido) {
    setForm(f => ({ ...f, valorRecebido: parseFloat(conta.data.valorParcela) || 0 }));
    setValorPreenchido(true);
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/upload", { method: "POST", credentials: "include", body: formData });
      if (!res.ok) throw new Error("Falha no upload");
      const data = await res.json();
      const tipo = file.name.endsWith(".xml") ? "xml" : file.name.endsWith(".pdf") ? "pdf" : "comprovante";
      await addArquivoMut.mutateAsync({ contaReceberId: id, nome: file.name, url: data.url, tipo });
      toast.success(`Arquivo "${file.name}" anexado!`);
    } catch (err: any) {
      toast.error(err.message || "Erro ao fazer upload");
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = () => {
    baixarMut.mutate({
      id,
      dataRecebimento: form.dataRecebimento,
      valorRecebido: Number(form.valorRecebido),
      anotacao: form.anotacao || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold">Dar Baixa</h2>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          {conta.data && (
            <div className="bg-gray-50 rounded p-3 text-sm space-y-1">
              {conta.data.pedidoId && <p><strong>Pedido:</strong> #{conta.data.pedidoId}</p>}
              {conta.data.numeroParcela && conta.data.totalParcelas && (
                <p><strong>Parcela:</strong> {conta.data.numeroParcela}/{conta.data.totalParcelas}</p>
              )}
              {conta.data.tipoPagamento && <p><strong>Modo de Pagamento:</strong> {conta.data.tipoPagamento}</p>}
              <p><strong>Valor:</strong> R$ {Number(conta.data.valorParcela).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
              <p><strong>Vencimento:</strong> {conta.data.dataVencimento ? new Date(conta.data.dataVencimento + "T12:00:00").toLocaleDateString("pt-BR") : "-"}</p>
            </div>
          )}
          <div>
            <Label>Data de Recebimento *</Label>
            <Input type="date" value={form.dataRecebimento} onChange={(e) => setForm({ ...form, dataRecebimento: e.target.value })} />
          </div>
          <div>
            <Label>Valor Recebido *</Label>
            <Input type="number" step="0.01" value={form.valorRecebido} onChange={(e) => setForm({ ...form, valorRecebido: parseFloat(e.target.value) || 0 })} />
          </div>
          <div>
            <Label>Anotação de baixa</Label>
            <textarea className="border rounded px-2 py-1.5 text-sm w-full h-16 resize-none" value={form.anotacao} onChange={(e) => setForm({ ...form, anotacao: e.target.value })} />
          </div>
          <div>
            <Label>Comprovante (opcional)</Label>
            <div className="flex items-center gap-2 mt-1">
              <Button type="button" variant="outline" size="sm" disabled={uploadingFile} onClick={() => fileInputRef.current?.click()}>
                {uploadingFile ? "Enviando..." : "Selecionar arquivo"}
              </Button>
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
            </div>
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={baixarMut.isPending || !form.dataRecebimento}>
            {baixarMut.isPending ? "Salvando..." : "Dar Baixa"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MODAL ANOTAÇÕES RECEBER
// ============================================================
function ModalAnotacoesReceber({ id, onClose }: { id: number; onClose: () => void }) {
  const utils = trpc.useUtils();
  const { data: anotacoes = [], isLoading } = trpc.accountsReceivable.anotacoes.list.useQuery({ contaReceberId: id });
  const [texto, setTexto] = useState("");
  const addAnotacaoMutation = trpc.accountsReceivable.anotacoes.add.useMutation({
    onSuccess: () => {
      setTexto("");
      utils.accountsReceivable.anotacoes.list.invalidate({ contaReceberId: id });
      toast.success("Anotação adicionada!");
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Anotações — Parcela #{id}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[55vh] overflow-y-auto">
          {isLoading ? (
            <p className="text-sm text-gray-500">Carregando...</p>
          ) : anotacoes.length === 0 ? (
            <p className="text-sm text-gray-500">Nenhuma anotação.</p>
          ) : (
            anotacoes.map((anotacao: any) => (
              <div key={anotacao.id} className="rounded border p-3 text-sm">
                <p className="whitespace-pre-wrap">{anotacao.texto}</p>
                <p className="mt-2 text-xs text-gray-500">
                  {anotacao.autorNome} · {anotacao.criadoEm ? new Date(anotacao.criadoEm).toLocaleString("pt-BR") : ""}
                </p>
              </div>
            ))
          )}
        </div>
        <div className="space-y-2">
          <Label>Nova anotação</Label>
          <textarea className="border rounded px-2 py-1.5 text-sm w-full h-20 resize-none" value={texto} onChange={(e) => setTexto(e.target.value)} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Fechar</Button>
            <Button onClick={() => addAnotacaoMutation.mutate({ contaReceberId: id, texto: texto.trim() })} disabled={addAnotacaoMutation.isPending || !texto.trim()}>
              {addAnotacaoMutation.isPending ? "Salvando..." : "Adicionar anotação"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// MODAL ARQUIVOS RECEBER
// ============================================================
function ModalArquivosReceber({ id, onClose }: { id: number; onClose: () => void }) {
  const utils = trpc.useUtils();
  const arquivos = trpc.accountsReceivable.arquivos.list.useQuery({ contaReceberId: id });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [visualizando, setVisualizando] = useState<any>(null);

  const addArquivoMut = trpc.accountsReceivable.arquivos.add.useMutation({
    onSuccess: () => { toast.success("Arquivo adicionado!"); utils.accountsReceivable.arquivos.list.invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  const deleteArquivoMut = trpc.accountsReceivable.arquivos.delete.useMutation({
    onSuccess: () => { toast.success("Arquivo removido!"); utils.accountsReceivable.arquivos.list.invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/upload", { method: "POST", credentials: "include", body: formData });
      if (!res.ok) throw new Error("Falha no upload");
      const data = await res.json();
      const tipo = file.name.endsWith(".xml") ? "xml" : file.name.endsWith(".pdf") ? "pdf" : "comprovante";
      await addArquivoMut.mutateAsync({ contaReceberId: id, nome: file.name, url: data.url, tipo });
    } catch (err: any) {
      toast.error(err.message || "Erro ao fazer upload");
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const isImage = (url: string) => /\.(png|jpg|jpeg|gif|webp)$/i.test(url);
  const isPdf = (url: string) => /\.pdf$/i.test(url);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold">Arquivos — Parcela #{id}</h2>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{arquivos.data?.length ?? 0} arquivo(s) anexado(s)</p>
            <Button type="button" variant="outline" size="sm" disabled={uploadingFile} onClick={() => fileInputRef.current?.click()}>
              {uploadingFile ? "Enviando..." : "Anexar arquivo"}
            </Button>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
          </div>

          {arquivos.data?.map?.((arq: any) => (
            <div key={arq.id} className="border rounded p-2 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium truncate flex-1">{arq.nome}</span>
                <div className="flex gap-1 flex-shrink-0">
                  <a href={arq.url} target="_blank" rel="noopener noreferrer" title="Baixar" className="text-blue-600 hover:text-blue-800">
                    <Download className="h-3.5 w-3.5" />
                  </a>
                  <button title="Remover" className="text-red-500 hover:text-red-700"
                    onClick={() => { if (confirm("Remover arquivo?")) deleteArquivoMut.mutate({ id: arq.id }); }}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-400">{new Date(arq.criadoEm).toLocaleString("pt-BR")}</p>
              {visualizando === arq.id ? (
                <div className="mt-1">
                  {isPdf(arq.url) && (
                    <iframe src={arq.url} className="w-full h-64 border rounded" title={arq.nome} />
                  )}
                  {isImage(arq.url) && (
                    <img src={arq.url} alt={arq.nome} className="max-w-full max-h-64 rounded border" />
                  )}
                  <button className="text-xs text-gray-500 mt-1 hover:underline" onClick={() => setVisualizando(null)}>Fechar visualização</button>
                </div>
              ) : (
                (isPdf(arq.url) || isImage(arq.url)) && (
                  <button className="text-xs text-blue-600 hover:underline" onClick={() => setVisualizando(arq.id)}>Visualizar</button>
                )
              )}
            </div>
          ))}

          {(!arquivos.data?.length) && (
            <p className="text-sm text-gray-400 text-center py-4">Nenhum arquivo anexado</p>
          )}
        </div>
        <div className="p-4 border-t flex justify-end flex-shrink-0">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </div>
  );
}
